import { useFormContext, useWatch } from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useEffect } from "react";

export type AutorizacionSeccionRadioProps = {
  name: string;
  title: string;
  description: React.ReactNode;
  withDetails?: boolean;
  detailLabel?: string;
  readOnly?: boolean;
};

export function AutorizacionSeccionRadio({
  name,
  title,
  description,
  withDetails,
  detailLabel,
  readOnly,
}: AutorizacionSeccionRadioProps) {
  const { control, setValue } = useFormContext();
  const inputName: string = withDetails ? `${name}.respuesta` : name;

  // 👀 Observar la respuesta seleccionada
  const respuesta = useWatch({
    control,
    name: `${inputName}`,
  });

   useEffect(() => {
    if (withDetails && respuesta === "no") {
      setValue(`${name}.detalle`, undefined);
    }
  }, [respuesta, withDetails, name, setValue]);

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="space-y-2">
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground whitespace-pre-line">
            {description}
          </p>
        </div>

        {/* 👇 Solo mostrar radios si NO es readOnly */}
        {!readOnly && (
          <FormField
            name={`${inputName}`}
            control={control}
            render={({ field }) => {
              return (
                <FormItem className="space-y-2">
                  <FormLabel>Seleccione una opción</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value || ""}
                      onValueChange={field.onChange}
                      className="flex gap-4"
                    >
                      {[
                        { label: "Sí", value: "si" },
                        { label: "No", value: "no" },
                      ].map((op) => (
                        <div
                          key={op.value}
                          className="flex items-center space-x-2"
                        >
                          <RadioGroupItem
                            value={op.value}
                            id={`${inputName}-${op.value}`}
                          />
                          <FormLabel htmlFor={`${inputName}-${op.value}`}>
                            {op.label}
                          </FormLabel>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        )}

        {/* Textarea condicional si aplica */}
        {withDetails && respuesta === "si" && (
          <FormField
            name={`${name}.detalle`}
            control={control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{detailLabel}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Detalle de quién ostenta esta calidad"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </CardContent>
    </Card>
  );
}
